import {
  type ApacCandidateHeaderFooterLinkProvider,
  mapLinkActionHelper,
} from '@seek/apac-candidate-header-footer';
import { type ComponentProps, useContext } from 'react';

import AnalyticsContext from 'src/analytics/AnalyticsContext';
import { LinkPosition } from 'src/analytics/adapter';
import type { HeaderFooterLinkContext } from 'src/analytics/adapter/headerFooter';

type LinkProviderValueType = ComponentProps<
  typeof ApacCandidateHeaderFooterLinkProvider
>['value'];

type SendLinkEventContext = Parameters<
  LinkProviderValueType['sendLinkEvent']
>['0']['linkContext'];

const isSendLinkEventContext = (
  context: unknown,
): context is SendLinkEventContext =>
  (context as SendLinkEventContext).openingOrClosing !== undefined;

export interface MapLinkContextToTrackingContextProps {
  linkContext: SendLinkEventContext;
}

export const mapLinkContextToTrackingContext = ({
  linkContext,
}: MapLinkContextToTrackingContextProps): HeaderFooterLinkContext => ({
  eventName: 'navigation_pressed',
  linkPosition: linkContext?.isExpanded
    ? LinkPosition.ExpandedMenu
    : (linkContext.position as LinkPosition.Header | LinkPosition.Footer),
  linkText: linkContext?.source || '',
  ...(isSendLinkEventContext(linkContext) && linkContext.openingOrClosing
    ? mapLinkActionHelper(linkContext.openingOrClosing!, {
        key: 'linkAction',
        opening: 'expand',
        closing: 'collapse',
      })
    : {}),
});

export const useHeaderFooterLinkProvider = (): LinkProviderValueType => {
  const { headerFooterNavigationPressed } = useContext(AnalyticsContext);
  return {
    sendLinkEvent: ({ linkContext }) => {
      const trackingContext = mapLinkContextToTrackingContext({
        linkContext,
      });
      headerFooterNavigationPressed({
        ...trackingContext,
        linkAction: trackingContext.linkAction!,
      });
    },
  };
};
